import React, { useState } from "react";
import { PersonFormData } from "../../types";
import DropdownSelect from "../common/DropdownSelect";
import "./groups.css";

interface MemberInvitationFormProps {
  onAddMember: (memberData: PersonFormData) => Promise<boolean>;
  existingMembers: Array<{
    id: number;
    name: string;
    email: string;
    isChild?: boolean;
  }>;
  isLoading?: boolean;
  error?: string;
  isEditing?: boolean;
  initialData?: PersonFormData & { isChild?: boolean };
}

const MemberInvitationForm: React.FC<MemberInvitationFormProps> = ({
  onAddMember,
  existingMembers,
  isLoading = false,
  error,
  isEditing = false,
  initialData,
}) => {
  const [formData, setFormData] = useState<PersonFormData>({
    name: initialData?.name || "",
    email: initialData?.email || "",
    parentPersonId: initialData?.parentPersonId,
  });

  const [isChild, setIsChild] = useState(initialData?.isChild || false);
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    email?: string;
    parentPersonId?: string;
  }>({});

  const validateForm = (): boolean => {
    const errors: { name?: string; email?: string; parentPersonId?: string } =
      {};

    if (!formData.name.trim()) {
      errors.name = "Il nome è obbligatorio";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Il nome deve essere di almeno 2 caratteri";
    }

    // Only validate email for adults
    if (!isChild) {
      if (!formData.email?.trim()) {
        errors.email = "L'email è obbligatoria";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = "Inserisci un indirizzo email valido";
      }
    }

    if (isChild && !formData.parentPersonId) {
      errors.parentPersonId = "Seleziona un genitore per questo bambino";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const memberData: PersonFormData = {
        ...formData,
        email: isChild ? "" : formData.email, // Empty email for children
        parentPersonId: isChild ? formData.parentPersonId : undefined,
      };

      const success = await onAddMember(memberData);

      // Reset form only after successful submission
      if (success) {
        setFormData({ name: "", email: "", parentPersonId: undefined });
        setIsChild(false);
        setValidationErrors({});
      }
    } catch (error) {
      // Error handling is done by parent component
    }
  };

  const handleInputChange = (
    field: keyof PersonFormData,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear validation error when user starts typing
    if (validationErrors[field as keyof typeof validationErrors]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleChildToggle = (checked: boolean) => {
    setIsChild(checked);
    if (!checked) {
      setFormData((prev) => ({ ...prev, parentPersonId: undefined }));
      setValidationErrors((prev) => ({ ...prev, parentPersonId: undefined }));
    } else {
      // Se diventa bambino, rimuovi l'email
      setFormData((prev) => ({ ...prev, email: "" }));
      setValidationErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  // Get adults (non-children) for parent selection
  const availableParents = existingMembers.filter((member) => !member.isChild);

  return (
    <div className="member-invitation-form">
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Nome sempre visibile */}
        <div className="form-group">
          <label htmlFor="memberName">Nome</label>
          <input
            id="memberName"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            placeholder="es. Mario Rossi, Sofia"
            disabled={isLoading}
            className={validationErrors.name ? "error" : ""}
          />
          {validationErrors.name && (
            <div className="field-error">{validationErrors.name}</div>
          )}
        </div>
        {/* Prima domanda: È un bambino? */}
        <div className="form-group">
          <label className="checkbox-label" style={{ textAlign: "center" }}>
            <input
              type="checkbox"
              checked={isChild}
              onChange={(e) => handleChildToggle(e.target.checked)}
              disabled={isLoading}
            />
            È un bambino? (sotto i 18 anni)
          </label>
        </div>
        {/* Se è un adulto, mostra email */}
        {!isChild && (
          <div className="form-group">
            <label htmlFor="memberEmail">Indirizzo Email</label>
            <input
              id="memberEmail"
              type="email"
              value={formData.email || ""}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="mario.rossi@example.com"
              disabled={isLoading}
              className={validationErrors.email ? "error" : ""}
            />
            {validationErrors.email && (
              <div className="field-error">{validationErrors.email}</div>
            )}
          </div>
        )}

        {/* Se è un bambino, mostra selezione genitore */}
        {isChild && (
          <div className="form-group">
            <label htmlFor="parentSelect">Genitore/Tutore</label>
            <DropdownSelect
              value={formData.parentPersonId?.toString() || ""}
              placeholder="Seleziona un genitore..."
              options={availableParents.map((parent) => ({
                value: parent.id.toString(),
                label: parent.name,
              }))}
              onChange={(value) =>
                handleInputChange(
                  "parentPersonId",
                  value ? Number(value) : undefined
                )
              }
              className={validationErrors.parentPersonId ? "error" : ""}
            />
            {validationErrors.parentPersonId && (
              <div className="field-error">
                {validationErrors.parentPersonId}
              </div>
            )}
            <div className="field-help">
              Le liste del bambino saranno gestite dal genitore/tutore.
            </div>
          </div>
        )}

        <button
          type="submit"
          className="add-member-button"
          disabled={isLoading}
        >
          {isLoading 
            ? (isEditing ? "Salvando..." : "Aggiungendo membro...") 
            : (isEditing ? "Salva Modifiche" : "Aggiungi Membro")}
        </button>
      </form>
    </div>
  );
};

export default MemberInvitationForm;
